import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Badge,
  Dropdown,
  List,
  Button,
  Typography,
  Spin,
  Empty,
  Tag,
  Avatar,
  Space,
  Divider,
  Tooltip,
  message,
  Modal,
  Descriptions,
} from "antd";
import SecureLogger from "../utils/secureLogger";
import {
  BellOutlined,
  EyeOutlined,
  DeleteOutlined,
  SettingOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  FireOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import useIsMobile from "../hooks/useIsMobile";
import apiClient from "../api/apiClient";
import { PLANNING_NOTIFICATION_EVENT } from "../utils/planningNotificationStream";
import BusinessAudioToggle from "./BusinessAudioToggle";
import { useNotificationUnread } from "../context/NotificationUnreadContext";
import {
  createScopedRequestGate,
  isScopedRequestCanceled,
} from "../utils/scopedRequestGate";
import {
  getNotificationAction,
  getNotificationDetailRows,
} from "../utils/notificationDetailDisplay";
import DailyRecapDetail, {
  DailyRecapModalTitle,
} from "./DailyRecapDetail";
import "./NotificationBell.css";
import "../styles/notification-details-modal.css";

const { Text, Title } = Typography;

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  priority: "low" | "medium" | "high" | "critical";
  created_at: string;
  is_read: boolean;
  is_dismissed: boolean;
  data?: any;
  warehouse_id?: number;
  warehouse_name?: string;
}

interface NotificationBellProps {
  className?: string;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ className }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { unreadCount, refreshUnreadCount, setUnreadCount } =
    useNotificationUnread();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [confirming, setConfirming] = useState(false);

  const authContext = useAuth() as { user: any } | null;
  const user = authContext?.user;
  const {
    selectedCompany: selectedCompanyId,
    selectedWarehouse: selectedWarehouseId,
  } = useSelection();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const notificationScopeKey = [
    user?.id || "none",
    selectedCompanyId || "none",
    selectedWarehouseId || "none",
  ].join(":");
  const notificationScopeRef = useRef({
    key: notificationScopeKey,
    generation: 0,
  });
  if (notificationScopeRef.current.key !== notificationScopeKey) {
    notificationScopeRef.current = {
      key: notificationScopeKey,
      generation: notificationScopeRef.current.generation + 1,
    };
  }
  const requestScopeKey = `${notificationScopeKey}@${notificationScopeRef.current.generation}`;
  const latestRequestScopeKeyRef = useRef(requestScopeKey);
  latestRequestScopeKeyRef.current = requestScopeKey;
  const listRequestGateRef = useRef<ReturnType<
    typeof createScopedRequestGate
  > | null>(null);
  if (!listRequestGateRef.current) {
    listRequestGateRef.current = createScopedRequestGate();
  }
  const refreshSequenceRef = useRef(0);

  // Fetch notifications
  const fetchNotifications = useCallback(
    async (showLoading = true) => {
      SecureLogger.debug("Fetching notifications");

      if (!user?.id || !selectedCompanyId) {
        SecureLogger.debug("Cannot fetch notifications - missing user or company");
        return;
      }

      const request = listRequestGateRef.current!.start(requestScopeKey);
      try {
        if (showLoading) setLoading(true);

        const params = {
          company_id: selectedCompanyId,
          user_id: user.id,
          warehouse_id: selectedWarehouseId || undefined,
          page: 1,
          limit: 20,
          sort_by: "created_at",
          sort_order: "DESC",
        };

        SecureLogger.debug("Calling notifications API");

        const response = await apiClient.get("/api/notifications", {
          params,
          skipGlobalLoading: !showLoading,
          signal: request.signal,
        });

        SecureLogger.debug("Notifications loaded successfully");

        if (request.isCurrent(latestRequestScopeKeyRef.current)) {
          setNotifications(response.data.notifications || []);
        }
      } catch (e) {
        const error = e as any;
        if (
          isScopedRequestCanceled(error, request.signal) ||
          !request.isCurrent(latestRequestScopeKeyRef.current)
        ) {
          return;
        }
        SecureLogger.error("Failed to fetch notifications");
        message.error("Erreur lors du chargement des notifications");
      } finally {
        const isCurrent = request.isCurrent(latestRequestScopeKeyRef.current);
        request.finish();
        if (isCurrent) setLoading(false);
      }
    },
    [requestScopeKey, user?.id, selectedCompanyId, selectedWarehouseId]
  );

  const reconcileAfterMutation = useCallback(() => {
    void fetchNotifications(false);
    void refreshUnreadCount({ suppressSound: true, forceFresh: true });
  }, [fetchNotifications, refreshUnreadCount]);

  // Mark notification as read
  const markAsRead = async (notificationId: number) => {
    const mutationScopeKey = requestScopeKey;
    try {
      await apiClient.post(`/api/notifications/mark-read/${notificationId}`, {
        user_id: user?.id,
        company_id: selectedCompanyId,
      });

      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;

      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );

      setUnreadCount((prev) => {
        return Math.max(0, prev - 1);
      });
      reconcileAfterMutation();
      message.success("Notification marquée comme lue");
    } catch (error) {
      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;
      SecureLogger.error("Failed to mark notification as read");
      message.error("Erreur lors du marquage");
    }
  };

  // Mark notification as unread
  const markAsUnread = async (notificationId: number) => {
    const mutationScopeKey = requestScopeKey;
    try {
      await apiClient.post(`/api/notifications/mark-unread/${notificationId}`, {
        user_id: user?.id,
        company_id: selectedCompanyId,
      });

      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;

      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId ? { ...notif, is_read: false } : notif
        )
      );

      setUnreadCount((prev) => {
        return prev + 1;
      });
      reconcileAfterMutation();
      message.success("Notification marquée comme non lue");
    } catch (error) {
      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;
      SecureLogger.error("Failed to mark notification as unread");
      message.error("Erreur lors du marquage");
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId: number) => {
    const mutationScopeKey = requestScopeKey;
    try {
      await apiClient.post(`/api/notifications/dismiss/${notificationId}`, {
        user_id: user?.id,
        company_id: selectedCompanyId,
      });

      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;

      setNotifications((prev) =>
        prev.filter((notif) => notif.id !== notificationId)
      );
      setUnreadCount((prev) => {
        const dismissedNotif = notifications.find(
          (n) => n.id === notificationId
        );
        const nextUnreadCount =
          dismissedNotif && !dismissedNotif.is_read
          ? Math.max(0, prev - 1)
          : prev;
        return nextUnreadCount;
      });

      reconcileAfterMutation();
      message.success("Notification écartée");
    } catch (error) {
      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;
      SecureLogger.error("Failed to dismiss notification");
      message.error("Erreur lors de l'écartement");
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    const mutationScopeKey = requestScopeKey;
    try {
      setRefreshing(true);
      await apiClient.post("/api/notifications/mark-all-read", {
        user_id: user?.id,
        company_id: selectedCompanyId,
        warehouse_id: selectedWarehouseId || undefined,
      });

      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;

      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, is_read: true }))
      );
      setUnreadCount(0);
      reconcileAfterMutation();
      message.success("Toutes les notifications marquées comme lues");
    } catch (error) {
      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;
      SecureLogger.error("Failed to mark all notifications as read");
      message.error("Erreur lors du marquage de toutes les notifications");
    } finally {
      if (latestRequestScopeKeyRef.current === mutationScopeKey) {
        setRefreshing(false);
      }
    }
  };

  // Mark all as unread
  const markAllAsUnread = async () => {
    const mutationScopeKey = requestScopeKey;
    try {
      setRefreshing(true);
      await apiClient.post("/api/notifications/mark-all-unread", {
        user_id: user?.id,
        company_id: selectedCompanyId,
        warehouse_id: selectedWarehouseId || undefined,
      });

      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;

      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, is_read: false }))
      );
      reconcileAfterMutation();
      message.success("Toutes les notifications marquées comme non lues");
    } catch (error) {
      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;
      SecureLogger.error("Failed to mark all notifications as unread");
      message.error("Erreur lors du marquage de toutes les notifications");
    } finally {
      if (latestRequestScopeKeyRef.current === mutationScopeKey) {
        setRefreshing(false);
      }
    }
  };

  // Refresh notifications
  const refreshNotifications = useCallback(() => {
    const refreshSequence = refreshSequenceRef.current + 1;
    refreshSequenceRef.current = refreshSequence;
    setRefreshing(true);
    Promise.all([
      fetchNotifications(false),
      refreshUnreadCount({ forceFresh: true }),
    ]).finally(() => {
      if (refreshSequenceRef.current === refreshSequence) {
        setRefreshing(false);
      }
    });
  }, [fetchNotifications, refreshUnreadCount]);

  // Get priority icon and color
  const getPriorityDisplay = (priority: string) => {
    switch (priority) {
      case "critical":
        return { icon: <FireOutlined />, color: "#ff4d4f", label: "Critique" };
      case "high":
        return {
          icon: <ExclamationCircleOutlined />,
          color: "#fa8c16",
          label: "Élevée",
        };
      case "medium":
        return {
          icon: <WarningOutlined />,
          color: "#fadb14",
          label: "Moyenne",
        };
      case "low":
        return {
          icon: <InfoCircleOutlined />,
          color: "#52c41a",
          label: "Faible",
        };
      default:
        return {
          icon: <InfoCircleOutlined />,
          color: "#173f5f",
          label: "Normale",
        };
    }
  };

  // Get notification type display
  const getTypeDisplay = (type: string) => {
    const typeMap: Record<string, { label: string; color: string }> = {
      stock_alert: { label: "Stock", color: "red" },
      payment_due: { label: "Paiement", color: "orange" },
      subscription_billing_alert: {
        label: "Souscription",
        color: "volcano",
      },
      order_pending: { label: "Commande", color: "blue" },
      production_alert: { label: "Production", color: "purple" },
      expense_alert: { label: "Dépense", color: "gold" },
      expense_supplier_missing: {
        label: "Fournisseur manquant",
        color: "orange",
      },
      expense_due_alert: {
        label: "Échéance aujourd’hui",
        color: "red",
      },
      payables_regularization_alert: {
        label: "À régulariser",
        color: "gold",
      },
      sales_receivables_alert: {
        label: "Factures à encaisser",
        color: "volcano",
      },
      user_action: { label: "Action", color: "green" },
      system_maintenance: { label: "Système", color: "gray" },
      report_ready: { label: "Rapport", color: "cyan" },
      transfer_received: { label: "Transfert Reçu", color: "green" },
      transfer_confirmed: { label: "Transfert Confirmé", color: "blue" },
      // 🛒 NOUVEAU : Types E-commerce
      ecommerce_new_order: { label: "Nouvelle Commande", color: "magenta" },
      ecommerce_status_change: { label: "Statut Commande", color: "blue" },
      ecommerce_cancellation: { label: "Annulation", color: "volcano" },
      restaurant_new_order: { label: "Commande Restaurant", color: "cyan" },
      // Nouveaux types : ventes, achats, ajustements, productions
      sale_completed: { label: "Vente", color: "green" },
      purchase_received: { label: "Achat", color: "geekblue" },
      stock_adjustment: { label: "Ajustement Stock", color: "orange" },
      stock_supply_practice_alert: {
        label: "Approvisionnement à vérifier",
        color: "gold",
      },
      production_completed: { label: "Production", color: "purple" },
      daily_recap: { label: "Bilan du jour", color: "gold" },
      planning_event: { label: "Planification équipe", color: "blue" },
    };

    return typeMap[type] || { label: type, color: "default" };
  };

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "À l'instant";
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes}min`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `Il y a ${diffInDays}j`;

    return date.toLocaleDateString("fr-FR");
  };

  // Render notification description (with custom rendering for transfer and ecommerce notifications)
  const renderNotificationDescription = (notification: Notification) => {
    if (notification.type === "restaurant_new_order" && notification.data) {
      try {
        const data =
          typeof notification.data === "string"
            ? JSON.parse(notification.data)
            : notification.data;

        return (
          <div className="notification-content">
            <Text type="secondary" ellipsis={{ tooltip: notification.message }}>
              {notification.message}
            </Text>
            <div
              style={{
                fontSize: "12px",
                marginTop: 4,
                color: "rgba(0, 0, 0, 0.65)",
              }}
            >
              <div>
                {data.source_label || "Restaurant"} -{" "}
                {data.table_label || data.service_label || "Service"}
              </div>
              <div>
                {data.items_count} article(s) -{" "}
                {data.total_amount?.toLocaleString("fr-FR")} FCFA
              </div>
              <div>{data.customer_name || "Client comptoir"}</div>
            </div>
            <div className="notification-time">
              <Text type="secondary" style={{ fontSize: "11px" }}>
                {formatRelativeTime(notification.created_at)}
              </Text>
            </div>
          </div>
        );
      } catch (e) {
        console.error("Error parsing restaurant notification data:", e);
      }
    }

    // 🛒 Custom rendering for e-commerce new order notifications
    if (notification.type === "ecommerce_new_order" && notification.data) {
      try {
        const data =
          typeof notification.data === "string"
            ? JSON.parse(notification.data)
            : notification.data;

        return (
          <div className="notification-content">
            <Text type="secondary" ellipsis={{ tooltip: notification.message }}>
              {notification.message}
            </Text>
            <div
              style={{
                fontSize: "12px",
                marginTop: 4,
                color: "rgba(0, 0, 0, 0.65)",
              }}
            >
              <div>
                🛍️ {data.items_count} produit(s) • {data.total_quantity} unité(s)
              </div>
              <div>💰 {data.total_amount?.toLocaleString("fr-FR")} FCFA</div>
              <div>👤 {data.customer_name}</div>
              {data.store_name && <div>🏪 {data.store_name}</div>}
            </div>
            <div className="notification-time">
              <Text type="secondary" style={{ fontSize: "11px" }}>
                {formatRelativeTime(notification.created_at)}
              </Text>
            </div>
          </div>
        );
      } catch (e) {
        console.error("Error parsing ecommerce notification data:", e);
      }
    }

    // 🛒 Custom rendering for e-commerce status change notifications
    if (notification.type === "ecommerce_status_change" && notification.data) {
      try {
        const data =
          typeof notification.data === "string"
            ? JSON.parse(notification.data)
            : notification.data;

        const statusEmojis: Record<string, string> = {
          pending: "⏳",
          confirmed: "✅",
          processing: "⚙️",
          shipped: "📦",
          delivered: "🎉",
          cancelled: "❌",
          refunded: "💰",
        };

        return (
          <div className="notification-content">
            <Text type="secondary" ellipsis={{ tooltip: notification.message }}>
              {notification.message}
            </Text>
            <div
              style={{
                fontSize: "12px",
                marginTop: 4,
                color: "rgba(0, 0, 0, 0.65)",
              }}
            >
              <div>
                {statusEmojis[data.old_status] || "📝"} {data.old_status} → {statusEmojis[data.new_status] || "📝"} {data.new_status}
              </div>
              <div>📋 {data.order_number}</div>
              <div>👤 {data.customer_name}</div>
            </div>
            <div className="notification-time">
              <Text type="secondary" style={{ fontSize: "11px" }}>
                {formatRelativeTime(notification.created_at)}
              </Text>
            </div>
          </div>
        );
      } catch (e) {
        console.error("Error parsing status change notification data:", e);
      }
    }

    // Custom rendering for transfer received notifications
    if (notification.type === "transfer_received" && notification.data) {
      try {
        const data =
          typeof notification.data === "string"
            ? JSON.parse(notification.data)
            : notification.data;

        return (
          <div className="notification-content">
            <Text type="secondary" ellipsis={{ tooltip: notification.message }}>
              {notification.message}
            </Text>
            <div
              style={{
                fontSize: "12px",
                marginTop: 4,
                color: "rgba(0, 0, 0, 0.65)",
              }}
            >
              <div>
                📦 {data.total_items} produit(s) • {data.total_quantity} unité(s)
              </div>
              <div>📍 De : {data.from_warehouse_name}</div>
              {data.transfer_number && <div>📋 Réf: {data.transfer_number}</div>}
            </div>
            <div className="notification-time">
              <Text type="secondary" style={{ fontSize: "11px" }}>
                {formatRelativeTime(notification.created_at)}
              </Text>
            </div>
          </div>
        );
      } catch (e) {
        // Fallback to default rendering if JSON parsing fails
        console.error("Error parsing notification data:", e);
      }
    }

    // Custom rendering for transfer confirmed notifications
    if (notification.type === "transfer_confirmed" && notification.data) {
      try {
        const data =
          typeof notification.data === "string"
            ? JSON.parse(notification.data)
            : notification.data;

        return (
          <div className="notification-content">
            <Text type="secondary" ellipsis={{ tooltip: notification.message }}>
              {notification.message}
            </Text>
            <div
              style={{
                fontSize: "12px",
                marginTop: 4,
                color: "rgba(0, 0, 0, 0.65)",
              }}
            >
              <div>
                📦 {data.total_items} produit(s) • {data.total_quantity} unité(s)
              </div>
              <div>📍 Vers : {data.to_warehouse_name}</div>
              {data.transfer_number && <div>📋 Réf: {data.transfer_number}</div>}
            </div>
            <div className="notification-time">
              <Text type="secondary" style={{ fontSize: "11px" }}>
                {formatRelativeTime(notification.created_at)}
              </Text>
            </div>
          </div>
        );
      } catch (e) {
        // Fallback to default rendering if JSON parsing fails
        console.error("Error parsing notification data:", e);
      }
    }

    // Default rendering for other notification types
    return (
      <div className="notification-content">
        <Text type="secondary" ellipsis={{ tooltip: notification.message }}>
          {notification.message}
        </Text>
        <div className="notification-time">
          <Text type="secondary" style={{ fontSize: "11px" }}>
            {formatRelativeTime(notification.created_at)}
          </Text>
        </div>
      </div>
    );
  };

  // Open notification details modal
  const handleOpenDetails = (notification: Notification) => {
    setSelectedNotification(notification);
    setDetailsModalVisible(true);
    // Mark as read when opened
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
  };

  // Navigate to all notifications page
  const handleViewAllNotifications = () => {
    setVisible(false);
    window.location.href = "/notifications";
  };

  // Confirm transfer reception
  const handleConfirmReception = async () => {
    if (!selectedNotification?.data) return;
    const mutationScopeKey = requestScopeKey;

    try {
      setConfirming(true);

      // Parse notification data to get order_id
      const data =
        typeof selectedNotification.data === "string"
          ? JSON.parse(selectedNotification.data)
          : selectedNotification.data;

      const orderId = data.order_id;

      if (!orderId) {
        message.error("ID de commande introuvable");
        return;
      }

      // Call confirmation API
      await apiClient.post(`/api/orders/${orderId}/confirm-reception`);

      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;

      message.success("Réception confirmée avec succès !");

      // Close modal and refresh notifications
      setDetailsModalVisible(false);
      await dismissNotification(selectedNotification.id); // Remove from list
    } catch (error: any) {
      if (latestRequestScopeKeyRef.current !== mutationScopeKey) return;
      SecureLogger.error("Failed to confirm reception", error);
      message.error(
        error.response?.data?.error || "Erreur lors de la confirmation"
      );
    } finally {
      if (latestRequestScopeKeyRef.current === mutationScopeKey) {
        setConfirming(false);
      }
    }
  };

  useEffect(() => {
    listRequestGateRef.current?.cancel();
    refreshSequenceRef.current += 1;
    setNotifications([]);
    setSelectedNotification(null);
    setDetailsModalVisible(false);
    setConfirming(false);
    setLoading(false);
    setRefreshing(false);
  }, [requestScopeKey]);

  // Refresh when dropdown opens or when its company/warehouse changes.
  useEffect(() => {
    if (!visible) return;
    const refresh = () => { void fetchNotifications(false); };
    window.addEventListener(PLANNING_NOTIFICATION_EVENT, refresh);
    return () => window.removeEventListener(PLANNING_NOTIFICATION_EVENT, refresh);
  }, [visible, fetchNotifications]);

  useEffect(() => {
    if (visible) {
      refreshNotifications();
      return;
    }

    listRequestGateRef.current?.cancel();
    refreshSequenceRef.current += 1;
    setLoading(false);
    setRefreshing(false);
  }, [refreshNotifications, visible]);

  useEffect(
    () => () => {
      listRequestGateRef.current?.cancel();
      refreshSequenceRef.current += 1;
    },
    []
  );

  const notificationDropdown = (
    <div className="notification-dropdown">
      <div className="notification-header">
        <div className="flex justify-between items-center">
          <Title level={5} style={{ margin: 0 }}>
            Notifications
            {unreadCount > 0 && (
              <Badge count={unreadCount} style={{ marginLeft: 8 }} />
            )}
          </Title>
          <Space>
            <BusinessAudioToggle
              alertType="notification_unread"
              label="Sons"
            />
            <Tooltip title="Actualiser">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined />}
                onClick={refreshNotifications}
                loading={refreshing}
              />
            </Tooltip>
            {unreadCount > 0 && (
              <Tooltip title="Tout marquer comme lu">
                <Button
                  type="text"
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={markAllAsRead}
                  loading={refreshing}
                />
              </Tooltip>
            )}
            <Tooltip title="Tout marquer comme non lu">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={markAllAsUnread}
                loading={refreshing}
              />
            </Tooltip>
            <Tooltip title="Paramètres de notification">
              <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
                onClick={() => {
                  setVisible(false);
                  if (selectedWarehouseId) {
                    window.location.href = `/notification-settings?warehouseId=${selectedWarehouseId}`;
                  } else {
                    message.warning(
                      "Veuillez sélectionner un établissement pour voir les paramètres."
                    );
                  }
                }}
                disabled={!selectedWarehouseId}
              />
            </Tooltip>
          </Space>
        </div>
      </div>

      <Divider style={{ margin: "8px 0" }} />

      <div className="notification-list">
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Aucune notification"
            style={{ padding: "20px" }}
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={notifications}
            renderItem={(item) => {
              const priorityDisplay = getPriorityDisplay(item.priority);
              const typeDisplay = getTypeDisplay(item.type);

              return (
                <List.Item
                  className={`notification-item ${
                    !item.is_read ? "unread" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleOpenDetails(item)}
                  actions={[
                    <Tooltip
                      title={
                        item.is_read
                          ? "Marquer comme non lu"
                          : "Marquer comme lu"
                      }
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={
                          item.is_read ? <EyeOutlined /> : <CheckOutlined />
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          item.is_read
                            ? markAsUnread(item.id)
                            : markAsRead(item.id);
                        }}
                      />
                    </Tooltip>,
                    <Tooltip title="Écarter">
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          dismissNotification(item.id);
                        }}
                        danger
                      />
                    </Tooltip>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={priorityDisplay.icon}
                        style={{ backgroundColor: priorityDisplay.color }}
                        size="small"
                      />
                    }
                    title={
                      <div className="notification-title">
                        <Text
                          strong={!item.is_read}
                          ellipsis={{ tooltip: item.title }}
                        >
                          {item.title}
                        </Text>
                        <Space size={4} style={{ marginLeft: 8 }}>
                          <Tag color={typeDisplay.color}>
                            {typeDisplay.label}
                          </Tag>
                          {item.warehouse_name && (
                            <Tag>{item.warehouse_name}</Tag>
                          )}
                        </Space>
                      </div>
                    }
                    description={renderNotificationDescription(item)}
                  />
                </List.Item>
              );
            }}
          />
        )}
      </div>

      {notifications.length > 0 && (
        <>
          <Divider style={{ margin: "8px 0" }} />
          <div className="notification-footer">
            <Button type="link" size="small" block onClick={handleViewAllNotifications}>
              Voir toutes les notifications
            </Button>
          </div>
        </>
      )}
    </div>
  );

  // Render detailed notification content based on type
  const renderDetailedContent = () => {
    if (!selectedNotification) return null;

    const priorityDisplay = getPriorityDisplay(selectedNotification.priority);
    const typeDisplay = getTypeDisplay(selectedNotification.type);

    // Parse data if it's a JSON string
    let parsedData = selectedNotification.data;
    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (e) {
        console.error("Error parsing notification data:", e);
      }
    }
    const genericDetailRows = parsedData
      ? getNotificationDetailRows(selectedNotification.type, parsedData)
      : [];

    if (selectedNotification.type === "daily_recap") {
      return (
        <DailyRecapDetail
          data={parsedData && typeof parsedData === "object" ? parsedData : {}}
          message={selectedNotification.message}
          createdAt={selectedNotification.created_at}
          warehouseNameFallback={selectedNotification.warehouse_name}
        />
      );
    }

    return (
      <div>
        <Space style={{ marginBottom: 16 }}>
          <Tag color={typeDisplay.color}>{typeDisplay.label}</Tag>
          <Tag color={priorityDisplay.color}>Priorité : {priorityDisplay.label}</Tag>
          {selectedNotification.warehouse_name && (
            <Tag icon={<SwapOutlined />}>{selectedNotification.warehouse_name}</Tag>
          )}
        </Space>

        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Résumé">
            <span style={{ whiteSpace: "pre-line" }}>
              {selectedNotification.message}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="Reçue le">
            {new Date(selectedNotification.created_at).toLocaleString("fr-FR", {
              dateStyle: "full",
              timeStyle: "short",
            })}
          </Descriptions.Item>
        </Descriptions>

        {/* 🛒 Custom rendering for e-commerce new order notifications */}
        {selectedNotification.type === "restaurant_new_order" && parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Détails de la Commande Restaurant</Title>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Numéro de commande" span={2}>
                {parsedData.order_number}
              </Descriptions.Item>
              <Descriptions.Item label="Source">
                {parsedData.source_label || parsedData.source_channel}
              </Descriptions.Item>
              <Descriptions.Item label="Service">
                {parsedData.table_label ||
                  parsedData.service_label ||
                  parsedData.service_mode}
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                {parsedData.customer_name || "Client comptoir"}
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">
                {parsedData.customer_phone || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Articles">
                {parsedData.items_count}
              </Descriptions.Item>
              <Descriptions.Item label="Quantité totale">
                {parsedData.total_quantity}
              </Descriptions.Item>
              <Descriptions.Item label="Montant total" span={2}>
                <Text strong style={{ fontSize: "16px", color: "#52c41a" }}>
                  {parsedData.total_amount?.toLocaleString("fr-FR")} FCFA
                </Text>
              </Descriptions.Item>
            </Descriptions>

            {parsedData.items && parsedData.items.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Articles commandés</Title>
                <List
                  size="small"
                  bordered
                  dataSource={parsedData.items}
                  renderItem={(item: any) => (
                    <List.Item>
                      <div style={{ width: "100%" }}>
                        <Text strong>{item.product_name}</Text>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                          <Text type="secondary">
                            Quantité: {item.quantity}
                          </Text>
                          <Text type="secondary">
                            Prix: {item.unit_price?.toLocaleString("fr-FR")} FCFA
                          </Text>
                          <Text strong>
                            Total: {item.subtotal?.toLocaleString("fr-FR")} FCFA
                          </Text>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {selectedNotification.type === "ecommerce_new_order" && parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Détails de la Commande E-commerce</Title>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Numéro de Commande" span={2}>
                {parsedData.order_number}
              </Descriptions.Item>
              <Descriptions.Item label="Boutique" span={2}>
                {parsedData.store_name}
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                {parsedData.customer_name}
              </Descriptions.Item>
              <Descriptions.Item label="Téléphone">
                {parsedData.customer_phone}
              </Descriptions.Item>
              {parsedData.customer_email && (
                <Descriptions.Item label="Email" span={2}>
                  {parsedData.customer_email}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Nombre de Produits">
                {parsedData.items_count}
              </Descriptions.Item>
              <Descriptions.Item label="Quantité Totale">
                {parsedData.total_quantity} unité(s)
              </Descriptions.Item>
              <Descriptions.Item label="Montant Total" span={2}>
                <Text strong style={{ fontSize: "16px", color: "#52c41a" }}>
                  {parsedData.total_amount?.toLocaleString("fr-FR")} FCFA
                </Text>
              </Descriptions.Item>
              {parsedData.shipping_address && (
                <Descriptions.Item label="Adresse de Livraison" span={2}>
                  {parsedData.shipping_address}
                </Descriptions.Item>
              )}
            </Descriptions>

            {parsedData.items && parsedData.items.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Produits Commandés</Title>
                <List
                  size="small"
                  bordered
                  dataSource={parsedData.items}
                  renderItem={(item: any) => (
                    <List.Item>
                      <div style={{ width: "100%" }}>
                        <Text strong>{item.product_name}</Text>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                          <Text type="secondary">
                            Quantité: {item.quantity}
                          </Text>
                          <Text type="secondary">
                            Prix unitaire: {item.unit_price?.toLocaleString("fr-FR")} FCFA
                          </Text>
                          <Text strong>
                            Total: {(item.quantity * item.unit_price)?.toLocaleString("fr-FR")} FCFA
                          </Text>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {/* 🛒 Custom rendering for e-commerce status change notifications */}
        {selectedNotification.type === "ecommerce_status_change" && parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Changement de Statut</Title>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Numéro de Commande" span={2}>
                {parsedData.order_number}
              </Descriptions.Item>
              <Descriptions.Item label="Boutique" span={2}>
                {parsedData.store_name}
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                {parsedData.customer_name}
              </Descriptions.Item>
              <Descriptions.Item label="Montant">
                {parsedData.total_amount?.toLocaleString("fr-FR")} FCFA
              </Descriptions.Item>
              <Descriptions.Item label="Ancien Statut">
                <Tag color="default">{parsedData.old_status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Nouveau Statut">
                <Tag color={parsedData.new_status === "delivered" ? "success" : parsedData.new_status === "cancelled" ? "error" : "processing"}>
                  {parsedData.new_status}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}

        {/* 🛒 Custom rendering for e-commerce cancellation notifications */}
        {selectedNotification.type === "ecommerce_cancellation" && parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Détails de l'Annulation</Title>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Numéro de Commande" span={2}>
                {parsedData.order_number}
              </Descriptions.Item>
              <Descriptions.Item label="Boutique" span={2}>
                {parsedData.store_name}
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                {parsedData.customer_name}
              </Descriptions.Item>
              <Descriptions.Item label="Montant">
                {parsedData.total_amount?.toLocaleString("fr-FR")} FCFA
              </Descriptions.Item>
              {parsedData.cancellation_reason && (
                <Descriptions.Item label="Raison de l'Annulation" span={2}>
                  <Text type="danger">{parsedData.cancellation_reason}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}

        {/* Custom rendering for transfer notifications */}
        {selectedNotification.type === "transfer_received" && parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Détails du Transfert</Title>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Numéro de Transfert" span={2}>
                {parsedData.transfer_number}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement Source">
                {parsedData.from_warehouse_name}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement Destination">
                {parsedData.to_warehouse_name}
              </Descriptions.Item>
              <Descriptions.Item label="Nombre de Produits">
                {parsedData.total_items}
              </Descriptions.Item>
              <Descriptions.Item label="Quantité Totale">
                {parsedData.total_quantity} unité(s)
              </Descriptions.Item>
              <Descriptions.Item label="Montant Total" span={2}>
                {parsedData.total_amount?.toLocaleString("fr-FR")} FCFA
              </Descriptions.Item>
            </Descriptions>

            {parsedData.items && parsedData.items.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Produits Transférés</Title>
                <List
                  size="small"
                  bordered
                  dataSource={parsedData.items}
                  renderItem={(item: any) => (
                    <List.Item>
                      <div style={{ width: "100%" }}>
                        <Text strong>{item.product_name}</Text>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                          <Text type="secondary">
                            Quantité: {item.quantity}
                          </Text>
                          <Text type="secondary">
                            Prix: {item.unit_price?.toLocaleString("fr-FR")} FCFA
                          </Text>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {/* Custom rendering for transfer confirmation notifications */}
        {selectedNotification.type === "transfer_confirmed" && parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Détails de la Confirmation</Title>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Numéro de Transfert" span={2}>
                {parsedData.transfer_number}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement Source (Vous)">
                {parsedData.from_warehouse_name}
              </Descriptions.Item>
              <Descriptions.Item label="Établissement Destinataire">
                {parsedData.to_warehouse_name}
              </Descriptions.Item>
              <Descriptions.Item label="Nombre de Produits">
                {parsedData.total_items}
              </Descriptions.Item>
              <Descriptions.Item label="Quantité Totale">
                {parsedData.total_quantity} unité(s)
              </Descriptions.Item>
              <Descriptions.Item label="Montant Total" span={2}>
                {parsedData.total_amount?.toLocaleString("fr-FR")} FCFA
              </Descriptions.Item>
            </Descriptions>

            {parsedData.items && parsedData.items.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>Produits du Transfert</Title>
                <List
                  size="small"
                  bordered
                  dataSource={parsedData.items}
                  renderItem={(item: any) => (
                    <List.Item>
                      <div style={{ width: "100%" }}>
                        <Text strong>{item.product_name}</Text>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                          <Text type="secondary">
                            Quantité: {item.quantity}
                          </Text>
                          <Text type="secondary">
                            Prix: {item.unit_price?.toLocaleString("fr-FR")} FCFA
                          </Text>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {/* Generic data display for other notification types */}
        {selectedNotification.type !== "transfer_received" &&
         selectedNotification.type !== "transfer_confirmed" &&
         selectedNotification.type !== "ecommerce_new_order" &&
         selectedNotification.type !== "ecommerce_status_change" &&
         selectedNotification.type !== "ecommerce_cancellation" &&
         selectedNotification.type !== "restaurant_new_order" &&
         selectedNotification.type !== "daily_recap" &&
         genericDetailRows.length > 0 &&
         parsedData && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>Détails utiles</Title>
            <Descriptions bordered column={1} size="small">
              {genericDetailRows.map((row) => (
                <Descriptions.Item key={row.key} label={row.label}>
                  {row.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </div>
        )}
      </div>
    );
  };

  const selectedNotificationData = (() => {
    if (!selectedNotification?.data) return null;
    try {
      return (
        typeof selectedNotification.data === "string"
          ? JSON.parse(selectedNotification.data)
          : selectedNotification.data
      );
    } catch (_) {
      return null;
    }
  })();
  const selectedNotificationAction =
    selectedNotification && selectedNotificationData
      ? getNotificationAction(
          selectedNotification.type,
          selectedNotificationData,
          selectedNotification.warehouse_id
        )
      : null;
  const isSelectedDailyRecap = selectedNotification?.type === "daily_recap";

  // 📱 Sur mobile : la cloche ne déploie pas le dropdown (trop à l'étroit) — elle
  // ouvre l'écran plein écran /notifications (rendu mobile via ResponsiveNotifications).
  if (isMobile) {
    return (
      <Badge count={unreadCount} overflowCount={99} size="small" offset={[-6, 6]}>
        <Button
          type="text"
          shape="circle"
          icon={<BellOutlined style={{ fontSize: "22px" }} />}
          size="large"
          className={`notification-bell ${className || ""}`}
          aria-label="Notifications"
          onClick={() => navigate("/notifications")}
        />
      </Badge>
    );
  }

  return (
    <>
      {/* Notification Details Modal */}
      <Modal
        className={`notification-details-modal${
          isSelectedDailyRecap ? " notification-details-modal--daily-recap" : ""
        }`}
        centered
        title={
          isSelectedDailyRecap ? (
            <DailyRecapModalTitle
              data={selectedNotificationData}
              createdAt={selectedNotification?.created_at}
              warehouseNameFallback={selectedNotification?.warehouse_name}
            />
          ) : (
            <div className="notification-modal-title">
              <Avatar
                icon={selectedNotification ? getPriorityDisplay(selectedNotification.priority).icon : <InfoCircleOutlined />}
                style={{
                  backgroundColor: selectedNotification
                    ? getPriorityDisplay(selectedNotification.priority).color
                    : "#173f5f",
                }}
                size={36}
              />
              <div className="notification-modal-title__text">
                {selectedNotification?.title || "Détails de la notification"}
              </div>
            </div>
          )
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={
          isSelectedDailyRecap
            ? [
                <Button key="close" onClick={() => setDetailsModalVisible(false)}>
                  Fermer
                </Button>,
                selectedNotificationAction && (
                  <Button
                    key="open-related"
                    type="primary"
                    onClick={() => {
                      setDetailsModalVisible(false);
                      navigate(selectedNotificationAction.url);
                    }}
                  >
                    {selectedNotificationAction.label}
                  </Button>
                ),
              ]
            : [
                selectedNotification?.type === "transfer_received" && (
                  <Button
                    key="confirm"
                    type="primary"
                    onClick={handleConfirmReception}
                    loading={confirming}
                    icon={<CheckOutlined />}
                  >
                    Confirmer réception
                  </Button>
                ),
                selectedNotificationAction && (
                  <Button
                    key="open-related"
                    type="primary"
                    onClick={() => {
                      setDetailsModalVisible(false);
                      navigate(selectedNotificationAction.url);
                    }}
                  >
                    {selectedNotificationAction.label}
                  </Button>
                ),
                <Button key="close" onClick={() => setDetailsModalVisible(false)}>
                  Fermer
                </Button>,
              ]
        }
        width={isSelectedDailyRecap ? 960 : 760}
      >
        {renderDetailedContent()}
      </Modal>

      <Dropdown
      overlay={notificationDropdown}
      visible={visible}
      onVisibleChange={setVisible}
      placement="bottomRight"
      trigger={["click"]}
      overlayClassName="notification-bell-dropdown"
    >
      <Badge
        count={unreadCount}
        overflowCount={99}
        size="small"
        offset={[-6, 6]}
      >
        <Button
          type="text"
          shape="circle"
          icon={<BellOutlined style={{ fontSize: "22px" }} />}
          size="large"
          className={`notification-bell ${className || ""}`}
        />
      </Badge>
    </Dropdown>
    </>
  );
};

export default NotificationBell;
